import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Heart, BadgeCheck, MessageCircle, Film, Trash2, Send, Loader2, Megaphone } from "lucide-react";
import api, { mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CheckoutModal from "@/components/CheckoutModal";
import { SupporterBadge } from "@/lib/badges";
import { toast } from "sonner";

export default function PostCard({ post, onUnlocked }) {
  const { user } = useAuth();
  const [justUnlocked, setJustUnlocked] = useState(false);
  const [liked, setLiked] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingC, setLoadingC] = useState(false);
  const [canComment, setCanComment] = useState(false);
  const [count, setCount] = useState(post.comment_count || 0);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const unlocked = justUnlocked || !!post.unlocked || !post.is_locked;
  const isVideo = post.media_type === "video";

  const toggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next) {
      setLoadingC(true);
      try {
        const { data } = await api.get(`/posts/${post.id}/comments`);
        setComments(data.comments);
        setCanComment(data.can_comment);
        setCount(data.count);
      } catch (e) {
        toast.error("Impossibile caricare i commenti");
      } finally {
        setLoadingC(false);
      }
    }
  };

  const addComment = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/comments`, { text });
      setComments((c) => [...c, data.comment]);
      setCount(data.count);
      setText("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally {
      setPosting(false);
    }
  };

  const removeComment = async (id) => {
    try {
      const { data } = await api.delete(`/comments/${id}`);
      setComments((c) => c.filter((x) => x.id !== id));
      setCount(data.count);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  };

  const confirmUnlock = async () => {
    try {
      await api.post("/unlocks", { post_id: post.id });
      setJustUnlocked(true);
      toast.success("Contenuto sbloccato!", { description: "Ora hai accesso permanente a questo contenuto." });
      onUnlocked && onUnlocked(post.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel pagamento");
      throw e;
    }
  };

  return (
    <div
      data-testid={`post-card-${post.id}`}
      className={`bg-[#1A1A1A] border rounded-3xl overflow-hidden card-hover-glow ${post.sponsored ? "border-[#F5C6D6]/40" : "border-white/10"}`}
    >
      {post.sponsored && (
        <div className="flex items-center gap-2 px-4 pt-3.5 -mb-1" data-testid={`post-sponsored-badge-${post.id}`}>
          <Megaphone className="w-3.5 h-3.5 text-[#F5C6D6]" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#F5C6D6]">Post in evidenza</span>
          <span className="ml-auto text-[9px] font-bold uppercase tracking-wider bg-[#F5C6D6] text-black px-2 py-0.5 rounded-full">ADV</span>
        </div>
      )}
      <div className="flex items-center gap-3 p-4">
        <img
          src={mediaUrl(post.creator_avatar)}
          alt={post.creator_name}
          className="w-10 h-10 rounded-full object-cover border border-white/10"
        />
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-sm">{post.creator_name}</span>
          {post.is_verified && <BadgeCheck className="w-4 h-4 text-[#F5C6D6] fill-[#F5C6D6]/20" />}
        </div>
      </div>

      <div className="relative aspect-square bg-black">
        {unlocked ? (
          isVideo ? (
            <video
              controls
              playsInline
              preload="metadata"
              data-testid={`video-player-${post.id}`}
              className="w-full h-full object-contain bg-black"
            >
              <source src={mediaUrl(post.media_url)} type="video/mp4" />
              {post.media_url_alt && <source src={mediaUrl(post.media_url_alt)} type="video/webm" />}
            </video>
          ) : (
            <img src={mediaUrl(post.media_url)} alt={post.caption} className="w-full h-full object-cover" />
          )
        ) : isVideo ? (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: "radial-gradient(circle at 50% 40%, rgba(233,30,140,0.35), #0D0D0D 70%)", filter: "blur(2px)" }}
          >
            <Film className="w-16 h-16 text-white/20" />
          </div>
        ) : (
          <img src={mediaUrl(post.media_url)} alt={post.caption} className="w-full h-full object-cover blur-2xl scale-110" />
        )}

        {isVideo && unlocked && (
          <span className="absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[11px] font-semibold pointer-events-none">
            <Film className="w-3 h-3" /> Video
          </span>
        )}

        {!unlocked && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/40 backdrop-blur-md">
            <div className="w-16 h-16 rounded-full bg-[#E91E8C] flex items-center justify-center glow-fuchsia">
              <Lock className="w-7 h-7 text-white" />
            </div>
            <p className="text-white/80 text-sm">{isVideo ? "Video bloccato" : "Contenuto bloccato"}</p>
            <button
              onClick={() => setCheckout(true)}
              data-testid={`unlock-btn-${post.id}`}
              className="px-6 py-2.5 rounded-full bg-white text-black font-semibold text-sm hover:bg-[#F5C6D6] transition-colors duration-300"
            >
              Sblocca per €{Number(post.price).toFixed(2)}
            </button>
          </div>
        )}
      </div>

      <div className="p-4">
        <p className="text-sm text-white/85 mb-3">{post.caption}</p>
        <div className="flex items-center gap-5 text-white/60">
          <button
            onClick={() => setLiked(!liked)}
            data-testid={`like-btn-${post.id}`}
            className="flex items-center gap-1.5 transition-colors duration-300 hover:text-[#E91E8C]"
          >
            <Heart className={`w-5 h-5 transition-transform ${liked ? "fill-[#E91E8C] text-[#E91E8C] scale-110" : ""}`} />
          </button>
          <button
            onClick={toggleComments}
            data-testid={`comment-btn-${post.id}`}
            className="flex items-center gap-1.5 transition-colors duration-300 hover:text-[#E91E8C]"
          >
            <MessageCircle className={`w-5 h-5 ${showComments ? "text-[#E91E8C]" : ""}`} />
            <span className="text-sm" data-testid={`comment-count-${post.id}`}>{count}</span>
          </button>
          <Link
            to={`/creator/${post.creator_id}`}
            className="ml-auto text-xs text-[#E91E8C] hover:underline"
            data-testid={`view-creator-${post.id}`}
          >
            Vedi profilo
          </Link>
        </div>

        {showComments && (
          <div className="mt-4 pt-4 border-t border-white/10" data-testid={`comments-panel-${post.id}`}>
            {loadingC ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#E91E8C]" /></div>
            ) : (
              <>
                <div className="space-y-3 mb-4">
                  {comments.length === 0 ? (
                    <p className="text-xs text-white/40" data-testid={`comments-empty-${post.id}`}>Nessun commento. Scrivi il primo!</p>
                  ) : (
                    comments.map((c) => (
                      <div key={c.id} data-testid={`comment-${c.id}`} className="flex gap-2.5 group/c">
                        <div className="w-8 h-8 rounded-full bg-[#E91E8C]/15 flex items-center justify-center shrink-0 overflow-hidden">
                          {c.author_avatar ? <img src={mediaUrl(c.author_avatar)} alt="" className="w-full h-full object-cover" /> : <span className="text-xs font-bold text-[#E91E8C]">{(c.author_name || "?")[0]}</span>}
                        </div>
                        <div className="flex-1 min-w-0 bg-[#0D0D0D] rounded-2xl px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold">{c.author_name}</span>
                            {c.author_badge && <SupporterBadge badge={c.author_badge} size="xs" />}
                            {c.can_delete && (
                              <button onClick={() => removeComment(c.id)} data-testid={`delete-comment-${c.id}`} className="ml-auto text-white/30 hover:text-red-400 opacity-0 group-hover/c:opacity-100 transition-opacity">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-white/80 mt-0.5 break-words">{c.text}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {!user ? (
                  <p className="text-xs text-white/40">Accedi per commentare.</p>
                ) : canComment ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={text} onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") addComment(); }}
                      data-testid={`comment-input-${post.id}`} placeholder="Scrivi un commento…"
                      className="flex-1 bg-[#0D0D0D] border border-white/10 rounded-full px-4 h-10 text-sm focus:border-[#E91E8C] focus:outline-none"
                    />
                    <button onClick={addComment} disabled={posting || !text.trim()} data-testid={`comment-submit-${post.id}`} className="w-10 h-10 rounded-full bg-[#E91E8C] hover:bg-[#D8157B] disabled:opacity-50 flex items-center justify-center text-white shrink-0">
                      {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-white/50 bg-[#0D0D0D] border border-white/10 rounded-full px-4 py-2.5" data-testid={`comment-locked-${post.id}`}>
                    <Lock className="w-3.5 h-3.5 text-[#E91E8C]" /> Sblocca il contenuto per commentare
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <CheckoutModal
        open={checkout}
        onOpenChange={setCheckout}
        title="Sblocca contenuto"
        subtitle={`Contenuto di ${post.creator_name}`}
        lines={[
          { label: "Creator", value: post.creator_name },
          { label: "Tipo", value: "Sblocco singolo" },
        ]}
        total={post.price}
        confirmLabel="Simula pagamento riuscito"
        onConfirm={confirmUnlock}
      />
    </div>
  );
}
